/**
 * X01 — session initialization needs a visible failure path.
 *
 * App.js called `client.auth.getSession().then(...)` with no rejection
 * handler, so a rejected lookup never set authReady and `if (!authReady)
 * return null` left a permanently blank page. A missing Supabase config also
 * fell through to the same "no auth needed" path, so a misconfigured
 * production build was indistinguishable from ordinary local dev.
 *
 * Five states, kept apart deliberately. Pure module: no React, no network.
 */

export const INITIALIZING = 'initializing';
export const AUTHENTICATED = 'authenticated';
export const SIGNED_OUT = 'signed_out';
export const FAILED = 'failed';              // the session check itself failed
export const UNCONFIGURED = 'unconfigured';  // auth isn't set up at all

/**
 * Decide the state from what we know.
 *
 *   hasClient  — a Supabase client could be constructed (config present)
 *   isProduction — a production build
 *   settled    — the session lookup has come back (either way)
 *   user       — the session's user, or false for "definitely signed out"
 *   error      — the session lookup rejected
 */
export function sessionState({ hasClient, isProduction, settled, user, error }) {
  if (!hasClient) return UNCONFIGURED;
  if (error) return FAILED;
  if (!settled) return INITIALIZING;
  return user ? AUTHENTICATED : SIGNED_OUT;
}

/**
 * Whether the app may render normally.
 *
 * Unconfigured is usable in development — that is how this repo runs locally,
 * with empty Supabase env vars — but never in production, where it means the
 * deploy is broken and must say so rather than silently serving an anonymous
 * workspace.
 */
export function canRenderApp(state, { isProduction } = {}) {
  if (state === AUTHENTICATED) return true;
  if (state === UNCONFIGURED) return !isProduction;
  return false;
}

/** A failure the user can act on, or null when there is nothing to say. */
export function sessionCopy(state, { isProduction } = {}) {
  switch (state) {
    case FAILED:
      return {
        tone: 'error',
        title: "We couldn't check whether you're signed in",
        body: 'This is usually a network blip. Nothing has been lost — any '
            + 'unsaved work on this device is still stored locally.',
        retry: 'Try again',
        signIn: 'Sign in again',
      };
    case UNCONFIGURED:
      return isProduction
        ? {
          tone: 'error',
          title: 'Sign-in is not configured on this deployment',
          body: 'This is a configuration problem, not a sign-in problem. '
              + 'Signing in again will not help — the deployment is missing its '
              + 'authentication settings.',
          retry: null,
          signIn: null,
        }
        : {
          tone: 'warning',
          title: 'Running without sign-in',
          body: 'No authentication is configured, so this is a local development '
              + 'session. Everything is scoped to the default workspace.',
          retry: null,
          signIn: null,
        };
    case INITIALIZING:
      return { tone: 'info', title: 'Checking your session…', body: '', retry: null, signIn: null };
    default:
      return null;
  }
}

/**
 * X01 — "Session failure must not turn a valid stored draft into an anonymous
 * blank workspace." Local drafts are only ever cleared on a deliberate
 * sign-out, never because a session check failed or auth is unconfigured.
 */
export function mayClearLocalDrafts({ reason }) {
  return reason === 'explicit_sign_out';
}
